import { useState } from 'react';
import { motion } from 'framer-motion';
import AppLayout from '@/components/feature/AppLayout';
import { useSubscriptions } from '@/hooks/useSubscriptions';
import { useApiKeys } from '@/hooks/useApiKeys';

const statusColor: Record<string, string> = {
  active:    'bg-green-50 text-green-600',
  expired:   'bg-red-50 text-red-500',
  cancelled: 'bg-gray-100 text-gray-500',
  pending:   'bg-amber-50 text-amber-600',
};

export default function SubscriptionsPage() {
  const [tab, setTab] = useState<'subscriptions' | 'keys'>('subscriptions');
  const { subscriptions, loading: subsLoading } = useSubscriptions();
  const { apiKeys, loading: keysLoading, revokeKey: revokeApiKey } = useApiKeys();
  const [copied, setCopied] = useState<string | null>(null);

  const copyKey = (id: string, val: string) => {
    navigator.clipboard.writeText(val);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const activeCount = subscriptions.filter(s => s.status === 'active').length;
  const lifetimeCount = subscriptions.filter(s => s.pricing_type === 'lifetime').length;
  const expiredCount = subscriptions.filter(s => s.status === 'expired').length;
  const pendingCount = subscriptions.filter(s => s.status === 'pending').length;

  return (
    <AppLayout>
      <div className="space-y-5">

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Active', value: activeCount, icon: 'ri-checkbox-circle-line', color: 'text-green-600 bg-green-50' },
            { label: 'Lifetime', value: lifetimeCount, icon: 'ri-infinity-line', color: 'text-brand-500 bg-brand-50' },
            { label: 'Expired', value: expiredCount, icon: 'ri-close-circle-line', color: 'text-red-500 bg-red-50' },
            { label: 'Pending', value: pendingCount, icon: 'ri-time-line', color: 'text-amber-500 bg-amber-50' },
          ].map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }} className="bg-white rounded-xl p-4">
              <span className={`w-9 h-9 flex items-center justify-center rounded-lg mb-3 ${s.color}`}><i className={`${s.icon} text-lg`} /></span>
              <div className="text-2xl font-bold text-gray-900">{subsLoading ? '—' : s.value}</div>
              <div className="text-xs text-gray-400">{s.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {(['subscriptions', 'keys'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-medium rounded-lg capitalize cursor-pointer whitespace-nowrap transition-colors ${tab === t ? 'bg-sidebar text-white' : 'bg-white border border-surface-3 text-gray-500 hover:border-gray-300'}`}>
              {t === 'keys' ? 'API Keys' : 'Subscriptions'}
            </button>
          ))}
        </div>

        {tab === 'subscriptions' && (
          <div className="space-y-4">
            {/* API Usage Meters */}
            {!subsLoading && subscriptions.filter(s => s.status === 'active').length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <i className="ri-dashboard-3-line text-brand-500" />
                  API Usage Meter — Current Billing Cycle
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {subscriptions.filter(s => s.status === 'active').map((s, i) => {
                    const pct = s.requests_limit > 0 ? Math.min(100, (s.requests_used / s.requests_limit) * 100) : 0;
                    const remaining = s.requests_limit - s.requests_used;
                    const isHigh = pct >= 80;
                    const isMed = pct >= 50 && pct < 80;
                    const barColor = isHigh ? 'bg-red-500' : isMed ? 'bg-amber-400' : 'bg-brand-500';
                    const bgColor = isHigh ? 'border-red-100 bg-red-50/30' : isMed ? 'border-amber-100 bg-amber-50/20' : 'border-surface-3';
                    return (
                      <motion.div key={s.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }} className={`bg-white rounded-xl p-4 border ${bgColor}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{s.product_name}</div>
                            <div className="text-[11px] text-gray-400 mt-0.5 capitalize">{s.pricing_type.replace(/_/g, '-')} plan</div>
                          </div>
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${isHigh ? 'bg-red-100 text-red-600' : isMed ? 'bg-amber-100 text-amber-600' : 'bg-brand-50 text-brand-600'}`}>
                            {Math.round(pct)}%
                          </span>
                        </div>
                        {/* Bar */}
                        <div className="w-full h-2.5 bg-surface-3 rounded-full overflow-hidden mb-2">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${pct}%` }}
                            transition={{ duration: 0.8, delay: i * 0.1 + 0.2 }}
                            className={`h-full rounded-full ${barColor}`}
                          />
                        </div>
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-gray-500">
                            <span className="font-semibold text-gray-800">{s.requests_used.toLocaleString()}</span> used
                          </span>
                          <span className="text-gray-500">
                            <span className={`font-semibold ${isHigh ? 'text-red-500' : 'text-gray-800'}`}>{remaining.toLocaleString()}</span> remaining
                          </span>
                          <span className="text-gray-400">of {s.requests_limit.toLocaleString()} total</span>
                        </div>
                        {isHigh && (
                          <div className="mt-2.5 flex items-center gap-1.5 text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-2.5 py-1.5">
                            <i className="ri-alarm-warning-line" />
                            <span className="font-medium">Usage critical — consider upgrading your plan</span>
                          </div>
                        )}
                        {isMed && !isHigh && (
                          <div className="mt-2.5 flex items-center gap-1.5 text-[11px] text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1.5">
                            <i className="ri-information-line" />
                            <span>Over halfway through your quota this cycle</span>
                          </div>
                        )}
                        <div className="mt-2 text-[11px] text-gray-400">
                          Expires: {s.end_date ? new Date(s.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : <span className="text-brand-500 font-medium">Lifetime</span>}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl overflow-hidden">
              {subsLoading ? (
                <div className="p-10 text-center text-sm text-gray-400">
                  <i className="ri-loader-4-line animate-spin text-2xl mb-2 block" />Loading subscriptions…
                </div>
              ) : subscriptions.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-12 h-12 bg-surface-2 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-key-2-line text-xl text-gray-400" />
                  </div>
                  <div className="text-sm font-medium text-gray-700 mb-1">No subscriptions yet</div>
                  <div className="text-xs text-gray-400">Subscriptions are created automatically when a payment is confirmed.</div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead>
                      <tr className="bg-surface-2 border-b border-surface-3">
                        {['User', 'Product', 'Plan', 'Usage', 'Status', 'Expires'].map(h => (
                          <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((s, i) => (
                        <motion.tr key={s.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.04 }} className="border-b border-surface-3 last:border-0 hover:bg-surface-2 transition-colors">
                          <td className="px-4 py-3">
                            <div className="text-xs font-medium text-gray-800">{s.user_name}</div>
                            <div className="text-[11px] text-gray-400">{s.user_email}</div>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600">{s.product_name}</td>
                          <td className="px-4 py-3">
                            <span className="text-[11px] bg-surface-2 text-gray-500 px-2 py-0.5 rounded-full capitalize">
                              {s.pricing_type.replace(/_/g, '-')}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-xs text-gray-700">{s.requests_used.toLocaleString()} / {s.requests_limit.toLocaleString()}</div>
                            <div className="w-28 h-1.5 bg-surface-3 rounded-full mt-1">
                              <div className="h-full bg-brand-500 rounded-full" style={{ width: `${Math.min(100, (s.requests_used / s.requests_limit) * 100)}%` }} />
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${statusColor[s.status] ?? 'bg-gray-100 text-gray-500'}`}>{s.status}</span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-400">
                            {s.end_date ? new Date(s.end_date).toLocaleDateString() : <span className="text-brand-500 font-medium">Lifetime</span>}
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'keys' && (
          <div className="space-y-3">
            {keysLoading ? (
              <div className="bg-white rounded-xl p-10 text-center text-sm text-gray-400">
                <i className="ri-loader-4-line animate-spin text-2xl mb-2 block" />Loading API keys…
              </div>
            ) : apiKeys.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <div className="w-12 h-12 bg-surface-2 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-key-line text-xl text-gray-400" />
                </div>
                <div className="text-sm font-medium text-gray-700 mb-1">No API keys yet</div>
                <div className="text-xs text-gray-400">Go to Developer Hub to generate your first API key.</div>
              </div>
            ) : (
              apiKeys.map((k, i) => (
                <motion.div key={k.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }} className="bg-white rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-gray-900">{k.product_name}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${k.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
                        {k.status}
                      </span>
                    </div>
                    <div className="font-mono text-xs text-gray-500 bg-surface-2 px-3 py-1.5 rounded-lg inline-block">
                      {k.key_prefix}••••••••
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1">
                      <i className="ri-bar-chart-line mr-1" />
                      {k.total_requests.toLocaleString()} requests · Created {new Date(k.created_at).toLocaleDateString()}
                      {k.last_used_at && ` · Last used ${new Date(k.last_used_at).toLocaleDateString()}`}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => copyKey(k.id, `${k.key_prefix}••••••••`)}
                      disabled={k.status === 'revoked'}
                      className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border transition-colors whitespace-nowrap cursor-pointer ${k.status === 'revoked' ? 'opacity-40 cursor-not-allowed border-surface-3 text-gray-400' : 'border-surface-3 text-gray-600 hover:bg-surface-2'}`}
                    >
                      <i className={copied === k.id ? 'ri-check-line text-green-500' : 'ri-file-copy-line'} />
                      {copied === k.id ? 'Copied!' : 'Copy'}
                    </button>
                    <button
                      onClick={() => revokeApiKey(k.id)}
                      disabled={k.status === 'revoked'}
                      className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border transition-colors whitespace-nowrap cursor-pointer ${k.status === 'revoked' ? 'opacity-40 cursor-not-allowed border-surface-3 text-gray-400' : 'border-red-100 text-red-500 hover:bg-red-50'}`}
                    >
                      <i className="ri-forbid-line" />Revoke
                    </button>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
